import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Guía para el funcionario operador de una alcaldía.
 * Cubre el día a día: chat ciudadano en tiempo real + bandeja
 * de PQRSDFs. Pensada para que el operador la lea una vez y
 * pueda arrancar sin acompañamiento.
 */
export function DocsOperadorPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Guía del operador
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Guía del operador
        </h1>

        <div className="callout">
          <strong>Tiempo de lectura:</strong> 20-30 min. Tenla abierta en otra
          pestaña durante tu primera semana atendiendo.
        </div>

        {/* ---- 1. Rol ---- */}
        <Section title="1. Tu rol como operador">
          <p>
            Eres la <strong>primera línea</strong> entre el ciudadano y la
            alcaldía. Tu trabajo tiene dos frentes:
          </p>
          <Ul
            items={[
              <>
                <strong>Chat ciudadano</strong> (web + WhatsApp): atender en
                tiempo real consultas que el bot no resolvió. La meta es que la
                mayoría termine resuelta sin escalar.
              </>,
              <>
                <strong>Bandeja de PQRSDFs</strong>: responder los radicados que
                te asignen, dentro de los plazos legales (Ley 1755/2015).
              </>,
            ]}
          />
          <p>
            Lo que <strong>NO</strong> es tu rol: reasignar trabajo entre
            compañeros (eso lo hace el supervisor), crear funcionarios o cambiar
            configuración del tenant (eso lo hace el admin).
          </p>
        </Section>

        {/* ---- 2. Primer login ---- */}
        <Section title="2. Primer login + cambiar contraseña">
          <Ol>
            <li>
              Abre <code>{`{slug}.mipuebloapp.com`}</code> (te lo dio el admin
              de la alcaldía)
            </li>
            <li>
              Login con tu <strong>email institucional</strong> + la{" "}
              <strong>contraseña temporal</strong> que te entregaron
            </li>
            <li>
              El sistema te obliga a <strong>cambiarla</strong> en el primer
              ingreso. Elige una nueva que cumpla:
              <ul>
                <li>Mínimo 10 caracteres</li>
                <li>Mezcla mayúscula, minúscula, número y símbolo</li>
                <li>
                  No reutilices la del correo personal — esta es para un sistema
                  de la alcaldía
                </li>
              </ul>
            </li>
            <li>
              Guárdala en un gestor de contraseñas (Bitwarden, 1Password, o el
              que use tu equipo TIC). <strong>NO</strong> la apuntes en
              post-its.
            </li>
          </Ol>
          <div className="callout warn">
            Si olvidas la contraseña, el admin del tenant tiene que generarte
            una temporal nueva — no hay reset por email por ahora.
          </div>
        </Section>

        {/* ---- 3. Disponibilidad ---- */}
        <Section title="3. Activar tu disponibilidad para chat">
          <p>
            El sistema no te asigna chats mientras estés marcado como{" "}
            <strong>no disponible</strong>. Tú controlas el switch.
          </p>
          <Ol>
            <li>
              Entra a <Kbd>Chat ciudadano</Kbd> en el sidebar
            </li>
            <li>
              Arriba a la derecha verás un toggle redondeado:
              <ul>
                <li>
                  <strong>○ No disponible</strong> (gris): no entran chats
                  nuevos a tu cola
                </li>
                <li>
                  <strong>● Disponible (N activos)</strong> (verde): puedes
                  tomar chats. El contador muestra cuántos tienes en curso.
                </li>
              </ul>
            </li>
            <li>Click en el toggle para alternar</li>
          </Ol>
          <div className="callout tip">
            💡 <strong>Buena práctica:</strong> apágalo cuando salgas a almorzar
            o a una reunión larga. Si lo dejas prendido y no respondes, el
            ciudadano se queda esperando.
          </div>
          <p>
            <strong>Nota:</strong> el admin del tenant puede prender/apagar tu{" "}
            <code>puede_chat</code> desde el panel de funcionarios. Si nunca te
            aparece el toggle, pídele al admin que lo active.
          </p>
        </Section>

        {/* ---- 4. Tomar de cola ---- */}
        <Section title="4. Tomar conversaciones de la cola">
          <p>
            En <Kbd>Chat ciudadano</Kbd> verás 4 pestañas en la columna
            izquierda:
          </p>
          <Table
            head={["Pestaña", "Qué contiene"]}
            rows={[
              [
                "Cola",
                "Ciudadanos esperando ser atendidos. El bot ya intentó y no resolvió.",
              ],
              ["Mis chats", "Los que TÚ estás atendiendo ahora."],
              [
                "Activas",
                "Todas las conversaciones activas del tenant (las tuyas + las de tus compañeros).",
              ],
              [
                "Cerradas",
                "Histórico — útil para repasar conversaciones pasadas.",
              ],
            ]}
          />
          <Ol>
            <li>
              Click en <Kbd>Cola</Kbd>
            </li>
            <li>
              Click en una conversación → se abre a la derecha con todo el
              transcript
            </li>
            <li>
              Lee el contexto (mensaje inicial + lo que dijo el bot)
            </li>
            <li>
              Click <Kbd>Tomar</Kbd> arriba a la derecha. La conversación pasa a{" "}
              <strong>Mis chats</strong> y el ciudadano ve que ya hay alguien
              atendiéndolo.
            </li>
          </Ol>
          <div className="callout">
            <strong>Regla del oro:</strong> si ves un chat en cola, tómalo o
            asume que un compañero lo hará. No dejes ciudadanos esperando más
            de 2-3 min.
          </div>
        </Section>

        {/* ---- 5. Chatear ---- */}
        <Section title="5. Chatear con el ciudadano">
          <p>Dentro de un chat en estado <code>activa</code> puedes:</p>
          <Ul
            items={[
              <>
                <strong>Escribir mensajes:</strong> caja de texto abajo. Enter
                envía, Shift+Enter hace salto de línea.
              </>,
              <>
                <strong>Recibir adjuntos:</strong> el ciudadano puede enviar
                imágenes, PDFs, audios. Aparecen como chips clickables — click
                abre un preview en modal (imagen, PDF, audio, video).
              </>,
              <>
                <strong>Ver el transcript completo</strong> en orden
                cronológico, incluyendo lo que dijo el bot antes de pasar a
                operador (mensajes en gris).
              </>,
              <>
                <strong>Mensajes de sistema</strong> aparecen centrados en
                cursiva (ej. "Conversación tomada por Juan Pérez").
              </>,
            ]}
          />
          <p>
            <strong>Tono recomendado:</strong> cercano pero formal. Trata de
            usted, sé directo, evita jerga jurídica innecesaria. El ciudadano
            normalmente no entiende términos como "subsanación" o "auto de
            archivo" — explica en lenguaje cotidiano.
          </p>
        </Section>

        {/* ---- 6. Cerrar resuelto ---- */}
        <Section title="6. Cerrar un chat resuelto">
          <p>Cuando consideres que el ciudadano quedó atendido:</p>
          <Ol>
            <li>
              Click <Kbd>Marcar resuelta</Kbd> arriba a la derecha (botón verde)
            </li>
            <li>
              Se abre un modal de confirmación — Enter o click{" "}
              <Kbd>Sí, marcar resuelta</Kbd>
            </li>
            <li>
              La conversación pasa a estado{" "}
              <strong>resuelta_operador</strong> y deja tus chats activos
            </li>
            <li>
              El sistema dispara <strong>automáticamente</strong> una breve
              encuesta al ciudadano (1-5 estrellas + si resolvió + comentario
              opcional)
            </li>
          </Ol>
          <div className="callout warn">
            <strong>No cierres si quedaste con dudas.</strong> Si el ciudadano
            puede volver con la misma consulta en 5 minutos, mejor seguí el
            hilo. Cerrar y reabrir genera dos conversaciones separadas y
            ensucia las métricas.
          </div>
        </Section>

        {/* ---- 7. Escalar a PQRSDF ---- */}
        <Section title="7. Escalar a PQRSDF cuando no se puede resolver">
          <p>
            Si la consulta requiere una <strong>respuesta formal</strong> de la
            alcaldía (revisión legal, decisión administrativa, certificación,
            etc.), no la cierres — escálala. Esto crea un radicado PQRSDF que
            corre con el SLA legal de Ley 1755/2015.
          </p>
          <Ol>
            <li>
              Click <Kbd>Escalar a PQRSDF</Kbd> arriba a la derecha
            </li>
            <li>
              Llenar el modal:
              <ul>
                <li>
                  <strong>Tipo:</strong> petición / queja / reclamo / sugerencia
                  / denuncia / felicitación. Si dudas → <code>peticion</code>.
                </li>
                <li>
                  <strong>Asunto:</strong> se rellena solo con las primeras
                  palabras del mensaje. Ajústalo a algo claro y corto (máx 200
                  caracteres).
                </li>
                <li>
                  <strong>Motivo de la escalación:</strong> 1-2 líneas
                  explicando por qué requiere respuesta formal (lo lee el
                  funcionario que tome el radicado).
                </li>
              </ul>
            </li>
            <li>
              Click <Kbd>Crear PQRSDF</Kbd>
            </li>
          </Ol>
          <p>
            El sistema hereda los datos del ciudadano y el{" "}
            <strong>transcript completo</strong> de la conversación. A partir
            de ese momento corre el plazo legal. La conversación queda como{" "}
            <strong>escalada_pqrsdf</strong> con link al radicado generado.
          </p>
          <div className="callout tip">
            💡 <strong>Tip:</strong> antes de escalar, dile al ciudadano algo
            como: "Voy a radicar formalmente su solicitud para que el área
            competente le responda. Le llegará el número de radicado por aquí
            mismo." Así no se siente abandonado.
          </div>
        </Section>

        {/* ---- 8. Bandeja PQRSDF ---- */}
        <Section title="8. Bandeja de PQRSDFs — filtros, prioridad, plazos">
          <p>
            En <Kbd>Mi bandeja</Kbd> ves los radicados ordenados por{" "}
            <strong>urgencia</strong> (más cerca del vencimiento → arriba).
          </p>

          <h4 style={{ marginTop: 20 }}>KPIs personales (chips superiores)</h4>
          <Table
            rows={[
              ["Asignados a mí", "Cuántos están en tu cola personal"],
              [
                "Vencidos",
                "PQRSDFs cuyo plazo ya pasó — son prioridad absoluta",
              ],
              [
                "Próximos a vencer",
                "Vencen en los próximos 3 días — atiéndelos hoy",
              ],
              [
                "Respondidos · 7d",
                "Cuántos sacaste en la última semana + tu tiempo promedio",
              ],
            ]}
          />

          <h4 style={{ marginTop: 20 }}>Filtros rápidos (chips de toolbar)</h4>
          <Ul
            items={[
              <><strong>Todos</strong> — vista global del tenant</>,
              <><strong>Mis radicados</strong> — solo los asignados a ti</>,
              <><strong>Próximos / vencidos</strong> — los que más urgen</>,
              <><strong>Radicados / En trámite / Respondidos</strong> — por estado</>,
              <>
                <strong>Filtro avanzado</strong> (icono embudo): combina tipo
                (petición/queja/...) + canal (web/WhatsApp/email/presencial)
              </>,
            ]}
          />

          <h4 style={{ marginTop: 20 }}>Lectura de la columna Plazo</h4>
          <Table
            head={["Color", "Significado", "Acción"]}
            rows={[
              ["Verde", "Más de 7 días", "Sin urgencia, ponlo en cola normal"],
              ["Amarillo", "Entre 1 y 7 días", "Empieza a trabajarlo esta semana"],
              ["Rojo", "Vence hoy o ya venció", "TIRA TODO Y RESPÓNDELO — el SLA legal corre"],
              ["Gris", "Cerrado", "Solo informativo, no requiere acción"],
            ]}
          />
        </Section>

        {/* ---- 9. Responder PQRSDF ---- */}
        <Section title="9. Responder un PQRSDF">
          <p>
            Desde la bandeja, click en un radicado o en el icono lápiz →
            entras a <Kbd>Responder</Kbd>.
          </p>

          <h4 style={{ marginTop: 20 }}>Asistente IA (borrador automático)</h4>
          <p>
            Si la alcaldía tiene IA activada, verás un banner arriba con un
            botón <Kbd>Generar borrador</Kbd>:
          </p>
          <Ol>
            <li>
              Click <Kbd>Generar borrador</Kbd> — la IA lee el radicado +
              normativa + plantillas y produce una propuesta de respuesta
            </li>
            <li>
              Click <Kbd>Insertar borrador</Kbd> → el texto entra al editor
            </li>
            <li>
              <strong>Revísalo línea por línea.</strong> La IA puede equivocarse
              en datos específicos (montos, fechas, nombres de áreas). NUNCA
              envíes sin revisar.
            </li>
            <li>
              Si la propuesta no sirve, click <Kbd>Regenerar</Kbd> para pedir
              otra desde cero
            </li>
          </Ol>

          <h4 style={{ marginTop: 20 }}>Plantillas aplicables</h4>
          <p>
            Panel derecho → <Kbd>Plantillas aplicables</Kbd>. El sistema filtra
            las que coinciden con el tipo del radicado (acuse, pedir info,
            trasladar, resuelto, etc.). Click en una → se inserta su contenido
            en el editor.
          </p>

          <h4 style={{ marginTop: 20 }}>Citar normativa</h4>
          <p>
            En la barra lateral derecha también verás{" "}
            <Kbd>Verificación legal</Kbd>: te marca si el borrador menciona la
            ley aplicable, si está dentro del plazo, etc. Si te indica algo
            faltante, corrígelo antes de firmar.
          </p>
        </Section>

        {/* ---- 10. Adjuntos + firma ---- */}
        <Section title="10. Subir adjuntos y firmar (PAdES)">
          <Ol>
            <li>
              En la página de respuesta, sección de adjuntos: arrastra los
              archivos o click <Kbd>Adjuntar</Kbd>
            </li>
            <li>
              Marca <Kbd>Adjuntar PDF de la respuesta</Kbd> si quieres que el
              cuerpo del editor se envíe también como PDF firmado (recomendado
              para respuestas formales)
            </li>
            <li>
              Click <Kbd>Previsualizar PDF</Kbd> para ver cómo le va a llegar
              al ciudadano antes de firmar
            </li>
            <li>
              Click <Kbd>Firmar y enviar</Kbd> (o{" "}
              <Kbd>Firmar y enviar por WhatsApp</Kbd> si el canal del radicado
              es WhatsApp)
            </li>
            <li>
              El sistema aplica <strong>firma PAdES</strong> al PDF — quedará
              con sello de tiempo y certificado del funcionario. Esa firma es
              la que da validez legal a la respuesta.
            </li>
          </Ol>
          <div className="callout warn">
            Una vez firmado y enviado, <strong>no se puede editar</strong>. Si
            te equivocaste tienes que mandar una corrección como respuesta
            adicional. Por eso revisa con calma antes de firmar.
          </div>
        </Section>

        {/* ---- 11. Plazos Ley 1755 ---- */}
        <Section title="11. Plazos legales (Ley 1755 de 2015)">
          <p>
            Todos los plazos son en <strong>días hábiles</strong> contados
            desde el día siguiente al radicado.
          </p>
          <Table
            head={["Tipo", "Plazo legal", "Notas"]}
            rows={[
              ["Petición general", "15 días hábiles", "El más común"],
              ["Petición de información", "10 días hábiles", "Cuando solo piden datos"],
              [
                "Petición de documentos",
                "10 días hábiles",
                "Copias / certificaciones",
              ],
              ["Queja / Reclamo", "15 días hábiles", "Igual que petición general"],
              [
                "Consultas (artículo 28)",
                "30 días hábiles",
                "Conceptos jurídicos especializados",
              ],
              [
                "Sugerencia",
                "15 días hábiles",
                "Aunque no obliga, hay que responder",
              ],
              [
                "Denuncia",
                "Variable",
                "Depende del trámite; suele ser 15 hábiles para acuse",
              ],
            ]}
          />
          <p>
            El sistema calcula la fecha de vencimiento sumando solo días
            hábiles colombianos (lun-vie + sin festivos del calendario
            nacional). El indicador de <strong>Plazo</strong> en la bandeja se
            actualiza automáticamente.
          </p>
          <div className="callout warn">
            <strong>Si el plazo vencerá:</strong> tienes opción de pedir{" "}
            <strong>prórroga</strong> antes del vencimiento, informando al
            ciudadano por escrito el motivo y la nueva fecha. Habla con tu
            supervisor antes de hacerlo.
          </div>
        </Section>

        {/* ---- 12. Buenas prácticas ---- */}
        <Section title="12. Buenas prácticas">
          <Checklist
            items={[
              <>
                <strong>Tono cercano-formal:</strong> trata de usted, lenguaje
                claro, sin jerga jurídica innecesaria
              </>,
              <>
                <strong>No inventes datos.</strong> Si no sabes el monto exacto
                del predial o el horario de una oficina, dilo: "voy a
                confirmar con el área y le respondo en X tiempo"
              </>,
              <>
                <strong>Cita la fuente:</strong> si das información normativa
                (artículo, decreto, resolución), menciona el número y año
              </>,
              <>
                <strong>Confirma datos personales antes de actuar:</strong> si
                el ciudadano pide algo sensible (consulta de un trámite,
                certificado), pídele documento de identidad
              </>,
              <>
                <strong>Una conversación, un asunto.</strong> Si el ciudadano
                trae 3 temas, ofrécele radicar 3 PQRSDFs separados para que
                cada uno tenga su SLA
              </>,
              <>
                <strong>Cierra siempre con próxima acción clara:</strong>{" "}
                "ya quedó radicado bajo el número X, le llegará respuesta a su
                correo antes del DD/MM"
              </>,
              <>
                <strong>Anota fuera del sistema lo que NO se debe escribir:</strong>{" "}
                datos médicos detallados, opiniones políticas. Lo que pongas
                queda en el expediente y es público en consulta ciudadana.
              </>,
            ]}
          />
        </Section>

        {/* ---- 13. Métricas ---- */}
        <Section title="13. Cómo te miden — métricas del operador">
          <p>
            El supervisor y el admin tienen un panel{" "}
            <Kbd>Métricas e informes</Kbd> que muestra tu desempeño. Las
            principales:
          </p>
          <Table
            head={["Métrica", "Qué mide", "Meta orientativa"]}
            rows={[
              [
                "Tiempo de primera respuesta",
                "Desde que tomas el chat o radicado hasta tu primer mensaje útil",
                "< 3 min en chat · < 24h en PQRSDF",
              ],
              [
                "% conversaciones resueltas",
                "Resueltas por operador / total que tomaste",
                "> 70%",
              ],
              [
                "% PQRSDFs respondidos en plazo",
                "Cumplimiento del SLA Ley 1755",
                "100% (legal)",
              ],
              [
                "NPS / rating de encuesta",
                "Calificación promedio que dan los ciudadanos al cerrar chat",
                "≥ 4 / 5",
              ],
              [
                "Volumen atendido",
                "Cuántos chats y PQRSDFs procesaste en el período",
                "Depende del tenant",
              ],
            ]}
          />
          <p>
            Puedes ver tu propio dashboard en <Kbd>Chat ciudadano</Kbd> →
            botón <Kbd>Métricas</Kbd> arriba a la derecha.
          </p>
          <div className="callout tip">
            💡 Si una encuesta llega con 1-2 estrellas, no lo tomes personal —
            úsala para mejorar. El supervisor probablemente la revise contigo
            para sacar aprendizajes.
          </div>
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
